/**
 * Submarine cable health data source.
 *
 * Fetches submarine cable geometry from TeleGeography and builds a landing-point
 * index (start/end of each cable route). Events are only emitted when a cable
 * has an advisory or fault status — no dots on the map means everything is healthy.
 *
 * The landing-point index is stored so that when a real cable monitoring feed is
 * integrated (Phase 2), advisories can be placed at the correct landing points
 * rather than arbitrary midpoints in the ocean.
 */

import type { Source, SourceMeta, ConfigField, SourceStatus, NormalizedEvent } from '../base-source';
import { djb2Hash } from '@/utils/hash';
import { getApiBase } from '@/app/api-router';

const SOURCE_ID = 'cable-health';

type CableStatus = 'fault' | 'degraded' | 'maintenance' | 'advisory';

interface CableFeature {
  type: string;
  properties: {
    id?: string;
    name?: string;
    color?: string;
    feature_id?: string;
    length?: string;
    owners?: string;
    url?: string;
    rfs?: string;
    is_planned?: boolean;
  };
  geometry: {
    type: string;
    coordinates: number[][] | number[][][];
  };
}

interface CableGeoJson {
  type: string;
  features?: CableFeature[];
}

interface LandingPoint {
  lat: number;
  lng: number;
  end: 'start' | 'end';
}

interface CableRecord {
  id: string;
  name: string;
  owners?: string;
  length?: string;
  rfs?: string;
  url?: string;
  isPlanned: boolean;
  landingPoints: LandingPoint[];
}

/** In-memory cable index — keyed by cable ID. */
const cableIndex = new Map<string, CableRecord>();

/** Active advisories — keyed by cable ID. Set externally or by a future monitoring feed. */
const activeAdvisories = new Map<string, { status: CableStatus; message: string; since: string }>();

let status: SourceStatus = {
  state: 'initializing',
  eventCount: 0,
};

/**
 * Extract landing points (start and end coordinates) from cable geometry.
 * These are where the cable meets land — the operationally relevant locations.
 */
function extractLandingPoints(geometry: CableFeature['geometry']): LandingPoint[] {
  const points: LandingPoint[] = [];
  const coords = geometry.coordinates;

  if (geometry.type === 'LineString' && coords.length >= 2) {
    const line = coords as number[][];
    const first = line[0];
    const last = line[line.length - 1];
    points.push({ lat: first[1], lng: first[0], end: 'start' });
    points.push({ lat: last[1], lng: last[0], end: 'end' });
  } else if (geometry.type === 'MultiLineString' && coords.length > 0) {
    const lines = coords as number[][][];
    const firstLine = lines[0];
    const lastLine = lines[lines.length - 1];
    if (firstLine.length >= 1) {
      points.push({ lat: firstLine[0][1], lng: firstLine[0][0], end: 'start' });
    }
    if (lastLine.length >= 1) {
      const lastPt = lastLine[lastLine.length - 1];
      points.push({ lat: lastPt[1], lng: lastPt[0], end: 'end' });
    }
  }

  return points.filter(p => isFinite(p.lat) && isFinite(p.lng));
}

/**
 * Register an advisory for a cable by name or ID.
 * Called externally when a monitoring feed detects an issue.
 */
export function setCableAdvisory(
  cableIdOrName: string,
  advisory: { status: CableStatus; message: string } | null,
): void {
  // Look up by ID first, then by name
  let id = cableIdOrName;
  if (!cableIndex.has(id)) {
    for (const [cid, record] of cableIndex) {
      if (record.name.toLowerCase() === cableIdOrName.toLowerCase()) {
        id = cid;
        break;
      }
    }
  }

  if (advisory) {
    activeAdvisories.set(id, { ...advisory, since: new Date().toISOString() });
  } else {
    activeAdvisories.delete(id);
  }
}

/** Get the current cable index for other modules. */
export function getCableIndex(): ReadonlyMap<string, CableRecord> {
  return cableIndex;
}

export const cableHealthSource: Source = {
  id: SOURCE_ID,

  meta: {
    name: 'Cable Health',
    description: 'Submarine cable advisory and fault monitoring at landing points',
    icon: 'cable',
    category: 'infrastructure',
    updateInterval: 1_800_000,
    requiresApiKey: false,
    dataLicense: 'Various',
    builtIn: true,
  } satisfies SourceMeta,

  configSchema: [] as ConfigField[],

  async initialize(): Promise<void> {
    status = { state: 'initializing', eventCount: 0 };
  },

  async start(): Promise<void> {
    status.state = 'active';
    console.log('[Sentinel] Cable health source started');
  },

  async stop(): Promise<void> {
    status.state = 'disabled';
  },

  async fetch(): Promise<NormalizedEvent[]> {
    try {
      const url = `${getApiBase()}/api/data/cable-health`;
      const response = await fetch(url, { signal: AbortSignal.timeout(20_000) });

      if (!response.ok) {
        throw new Error(`Cable data API returned ${response.status}`);
      }

      const data: CableGeoJson = await response.json();

      if (!data.features || !Array.isArray(data.features)) {
        return [];
      }

      // Rebuild the cable index from fresh geometry data
      cableIndex.clear();
      for (const feature of data.features) {
        const props = feature.properties;
        if (!props.name) continue;

        const id = String(props.id ?? props.feature_id ?? djb2Hash(props.name));
        const landingPoints = extractLandingPoints(feature.geometry);

        cableIndex.set(id, {
          id,
          name: props.name,
          owners: props.owners,
          length: props.length,
          rfs: props.rfs,
          url: props.url,
          isPlanned: props.is_planned ?? false,
          landingPoints,
        });
      }

      // Only emit events for cables with active advisories
      const events: NormalizedEvent[] = [];
      const now = new Date().toISOString();

      for (const [cableId, advisory] of activeAdvisories) {
        const cable = cableIndex.get(cableId);
        if (!cable) continue;

        const severityMap: Record<CableStatus, NormalizedEvent['severity']> = {
          fault: 'critical',
          degraded: 'high',
          maintenance: 'info',
          advisory: 'medium',
        };

        // Create an event at each landing point so analysts can see both ends affected
        for (const lp of cable.landingPoints) {
          events.push({
            id: `cable-${cableId}-${lp.end}`,
            source: SOURCE_ID,
            timestamp: advisory.since,
            type: 'infrastructure',
            title: `${cable.name} — ${advisory.status.toUpperCase()}`,
            description: advisory.message,
            severity: severityMap[advisory.status],
            location: { lat: lp.lat, lng: lp.lng, name: `${cable.name} (${lp.end} landing)` },
            tags: ['submarine-cable', 'infrastructure', advisory.status],
            links: cable.url ? [{ url: cable.url, label: 'Cable Info' }] : [],
            raw: {
              cableId,
              cableName: cable.name,
              cableStatus: advisory.status,
              advisoryMessage: advisory.message,
              landingEnd: lp.end,
              owners: cable.owners,
              length: cable.length,
              rfs: cable.rfs,
            },
          });
        }
      }

      status.state = 'active';
      status.lastFetch = now;
      status.eventCount = events.length;
      return events;
    } catch (err) {
      status.state = 'error';
      status.lastError = err instanceof Error ? err.message : String(err);
      throw err;
    }
  },

  getStatus(): SourceStatus {
    return { ...status };
  },
};
